import { Helmet } from 'react-helmet';
import { useState, useEffect } from 'react';
import { Text, Button } from '@chakra-ui/react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { PageHeader } from 'shared-components';

import { ApplicationState } from '../../../../store';
import Finger, { Status } from '../../../atoms/finger';
import { createNavigateToAction } from '../../../../middleware/actions';
import ChecklistSection from '../../../atoms/checklist-section';
import { VISA_PREP_FORM } from '../../../../routes';
import { visaPrepFlags } from '../../../../utilities/pages';
import PageSubtext from '../../../atoms/PageSubtext';
import { titleAppend } from '../../../../utilities/head';

const VisaPrepForm = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onResume = (url: string) => dispatch(createNavigateToAction(url));

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const {
    nonHomeCountryApplication,
    hasTravelledToUk,
    hasLeaveToRemain,
    hasBorderRefusal,
    hasOtherTravellers,
    hasTakenEnglishTest
  } = visaPrepFlags(student.personalInfo, student.history, student.offer);

  // constants

  // component state
  const [VPF_API_1, setVPF_API_1] = useState(Status.Todo);
  const [VPF_API_2, setVPF_API_2] = useState(Status.Todo);
  const [VPF_AH_1, setVPF_AH_1] = useState(Status.Todo);
  const [VPF_AH_2, setVPF_AH_2] = useState(Status.Todo);
  const [VPF_AH_3, setVPF_AH_3] = useState(Status.Todo);
  const [VPF_AH_4, setVPF_AH_4] = useState(Status.Todo);
  const [VPF_AH_5, setVPF_AH_5] = useState(Status.Todo);
  const [VPF_AH_6, setVPF_AH_6] = useState(Status.Todo);
  const [VPF_AH_7, setVPF_AH_7] = useState(Status.Todo);
  const [VPF_AH_8, setVPF_AH_8] = useState(Status.Todo);
  const [VPF_F_1, setVPF_F_1] = useState(Status.Todo);
  const [VPF_F_2, setVPF_F_2] = useState(Status.Todo);
  const [VPF_F_3, setVPF_F_3] = useState(Status.Todo);
  const [VPF_F_4, setVPF_F_4] = useState(Status.Todo);
  const [VPF_F_5, setVPF_F_5] = useState(Status.Todo);
  const [VPF_OT_1, setVPF_OT_1] = useState(Status.Todo);
  const [VPF_A_1, setVPF_A_1] = useState(Status.Todo);
  const [VPF_D_1, setVPF_D_1] = useState(Status.Todo);
  const [VPF_UE_1, setVPF_UE_1] = useState(Status.Todo);
  const [VPF_UE_2, setVPF_UE_2] = useState(Status.Todo);
  const [VPF_UE_3, setVPF_UE_3] = useState(Status.Todo);
  const [VPF_FB_1, setVPF_FB_1] = useState(Status.Todo);

  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (student && student.visa !== undefined) {
      const {
        isPage1Complete = false,
        isPage1_2Complete = false,
        isPage2_1Complete = false,
        isPage2_2Complete = false,
        isPage2_3Complete = false,
        isPage2_4Complete = false,
        isPage2_5Complete = false,
        isPage2_6Complete = false,
        isPage2_7Complete = false,
        isPage2_8Complete = false,
        isPage3_1Complete = false,
        isPage3_2Complete = false,
        isPage3_3Complete = false,
        isPage3_4Complete = false,
        isPage3_5Complete = false,
        isPage4Complete = false,
        isPage5Complete = false,
        isPage6Complete = false,
        isPage7_1Complete = false,
        isPage7_2Complete = false,
        isPage7_3Complete = false,
        isPage8Complete = false
      } = student.visa;
      setVPF_API_1(isPage1Complete ? Status.Complete : Status.InProgress);
      setVPF_API_2(isPage1_2Complete ? Status.Complete : isPage1Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_1(
        isPage2_1Complete ? Status.Complete : isPage1Complete && isPage1_2Complete ? Status.InProgress : Status.Todo
      );
      setVPF_AH_2(
        isPage2_2Complete
          ? Status.Complete
          : (nonHomeCountryApplication === true && isPage2_1Complete) ||
            (nonHomeCountryApplication === false && isPage1Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_AH_3(isPage2_3Complete ? Status.Complete : isPage2_2Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_4(
        isPage2_4Complete
          ? Status.Complete
          : (hasTravelledToUk === true && isPage2_3Complete) || (hasTravelledToUk === false && isPage2_2Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_AH_5(isPage2_5Complete ? Status.Complete : isPage2_4Complete ? Status.InProgress : Status.Todo);
      setVPF_AH_6(
        isPage2_6Complete
          ? Status.Complete
          : (hasLeaveToRemain === true && isPage2_5Complete) || (hasLeaveToRemain === false && isPage2_4Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_AH_7(
        isPage2_7Complete
          ? Status.Complete
          : (hasBorderRefusal === true && isPage2_6Complete) ||
            (hasBorderRefusal === false && isPage2_5Complete) ||
            (hasBorderRefusal === false && hasLeaveToRemain === false && isPage2_4Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_AH_8(isPage2_8Complete ? Status.Complete : isPage2_7Complete ? Status.InProgress : Status.Todo);
      setVPF_F_1(isPage3_1Complete ? Status.Complete : isPage2_8Complete ? Status.InProgress : Status.Todo);
      setVPF_F_2(isPage3_2Complete ? Status.Complete : isPage3_1Complete ? Status.InProgress : Status.Todo);
      setVPF_F_3(isPage3_3Complete ? Status.Complete : isPage3_2Complete ? Status.InProgress : Status.Todo);
      setVPF_F_4(isPage3_4Complete ? Status.Complete : isPage3_3Complete ? Status.InProgress : Status.Todo);
      setVPF_F_5(isPage3_5Complete ? Status.Complete : isPage3_4Complete ? Status.InProgress : Status.Todo);
      setVPF_OT_1(isPage4Complete ? Status.Complete : isPage3_5Complete ? Status.InProgress : Status.Todo);
      setVPF_A_1(
        isPage5Complete
          ? Status.Complete
          : (hasOtherTravellers === true && isPage4Complete) || (hasOtherTravellers === false && isPage3_4Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_D_1(isPage6Complete ? Status.Complete : isPage5Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_1(isPage7_1Complete ? Status.Complete : isPage6Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_2(isPage7_2Complete ? Status.Complete : isPage7_1Complete ? Status.InProgress : Status.Todo);
      setVPF_UE_3(
        isPage7_3Complete
          ? Status.Complete
          : (hasTakenEnglishTest === false && isPage7_2Complete) || (hasTakenEnglishTest === true && isPage7_1Complete)
          ? Status.InProgress
          : Status.Todo
      );
      setVPF_FB_1(isPage8Complete ? Status.Complete : isPage7_3Complete ? Status.InProgress : Status.Todo);
      setStarted(!!student.visa.isSectionStarted);
    }
  }, [
    hasBorderRefusal,
    hasLeaveToRemain,
    hasOtherTravellers,
    hasTakenEnglishTest,
    hasTravelledToUk,
    nonHomeCountryApplication,
    student
  ]);

  const pageTitle = 'Draft Visa Application';

  return (
    <>
      <Helmet>
        <title>
          {pageTitle} {titleAppend}
        </title>
      </Helmet>

      <PageHeader dataSelector="Dashboard-Header">{pageTitle}</PageHeader>

      <PageSubtext>
        <Text>
          In this section you will be asked follow up questions that will help us facilitate the best possible visa
          application for you.
        </Text>

        <Text>
          Note: Your answers will not go directly to your application, they will first be reviewed by your visa
          consultant who will prepare your final application with you.
        </Text>

        <Text>
          To ensure a speedy process we recommend you keep your responses as accurate as possible, but you will have the
          opportunity to improve your answers before submission.
        </Text>
      </PageSubtext>

      {!started ? (
        <Button
          id="save_and_continue"
          onClick={() => onResume(`/${VISA_PREP_FORM}/additional-personal-info/step-1`)}
          variant="solid"
          size="lg"
        >
          Start visa preparation
        </Button>
      ) : (
        <>
          <ChecklistSection label={'Section 1 - Additional Personal information'}>
            <Finger
              id={'VPF_API_1'}
              status={VPF_API_1}
              //hasStart={!started}
              label={'Passport'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-personal-info/step-1`)}
            />

            <Finger
              id={'VPF_API_2'}
              status={VPF_API_2}
              label={'Home address'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-personal-info/step-2`)}
            />
          </ChecklistSection>

          <ChecklistSection label={'Section 2 - Additional History'}>
            {nonHomeCountryApplication === true && (
              <Finger
                id={'VPF_AH_1'}
                status={VPF_AH_1}
                label={'Applying outside home country'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-1`)}
              />
            )}

            <Finger
              id={'VPF_AH_2'}
              status={VPF_AH_2}
              label={'Overseas visas'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-2`)}
            />

            {hasTravelledToUk === true && (
              <Finger
                id={'VPF_AH_3'}
                status={VPF_AH_3}
                label={'UK Travel'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-3`)}
              />
            )}

            <Finger
              id={'VPF_AH_4'}
              status={VPF_AH_4}
              label={'Previous UK activities'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-4`)}
            />

            {hasLeaveToRemain === true && (
              <Finger
                id={'VPF_AH_5'}
                status={VPF_AH_5}
                label={'Leave to Remain'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-5`)}
              />
            )}

            {hasBorderRefusal === true && (
              <Finger
                id={'VPF_AH_6'}
                status={VPF_AH_6}
                label={'UK border refusal'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-6`)}
              />
            )}

            <Finger
              id={'VPF_AH_7'}
              status={VPF_AH_7}
              label={'Deportation and removal'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-7`)}
            />

            <Finger
              id={'VPF_AH_8'}
              status={VPF_AH_8}
              label={'Travel (excluding the UK)'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/additional-history/step-8`)}
            />
          </ChecklistSection>

          <ChecklistSection label={'Section 3 - Family'}>
            <Finger
              id={'VPF_F_1'}
              status={VPF_F_1}
              label={'Spouse or partner details'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/family/step-1`)}
            />

            <Finger
              id={'VPF_F_2'}
              status={VPF_F_2}
              label={`Father's details`}
              onClick={() => onResume(`/${VISA_PREP_FORM}/family/step-2`)}
            />

            <Finger
              id={'VPF_F_3'}
              status={VPF_F_3}
              label={`Mother's details`}
              onClick={() => onResume(`/${VISA_PREP_FORM}/family/step-3`)}
            />

            <Finger
              id={'VPF_F_4'}
              status={VPF_F_4}
              label={`Guardian details`}
              onClick={() => onResume(`/${VISA_PREP_FORM}/family/step-4`)}
            />

            <Finger
              id={'VPF_F_5'}
              status={VPF_F_5}
              label={`Dependant children`}
              onClick={() => onResume(`/${VISA_PREP_FORM}/family/step-5`)}
            />
          </ChecklistSection>

          {hasOtherTravellers === true && (
            <ChecklistSection label={'Section 4 - Other travellers'}>
              <Finger
                id={'VPF_OT_1'}
                status={VPF_OT_1}
                label={'Traveller details'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/other-travellers/step-1`)}
              />
            </ChecklistSection>
          )}
          <ChecklistSection label={'Section 5 - Arrival'}>
            <Finger
              id={'VPF_A_1'}
              status={VPF_A_1}
              label={'UK contacts details'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/arrival/step-1`)}
            />
          </ChecklistSection>

          <ChecklistSection label={'Section 6 - Declarations'}>
            <Finger
              id={'VPF_D_1'}
              status={VPF_D_1}
              label={'Declarations'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/declarations/step-1`)}
            />
          </ChecklistSection>

          <ChecklistSection label={'Section 7 - University Enrolment'}>
            <Finger
              id={'VPF_UE_1'}
              status={VPF_UE_1}
              label={'CAS and course'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/uni-enrolment/step-1`)}
            />

            {hasTakenEnglishTest === false && (
              <Finger
                id={'VPF_UE_2'}
                status={VPF_UE_2}
                label={'English test'}
                onClick={() => onResume(`/${VISA_PREP_FORM}/uni-enrolment/step-2`)}
              />
            )}

            <Finger
              id={'VPF_UE_3'}
              status={VPF_UE_3}
              label={'UCAS'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/uni-enrolment/step-3`)}
            />
          </ChecklistSection>

          <ChecklistSection label={'Section 8 - Feedback'}>
            <Finger
              id={'VPF_FB_1'}
              status={VPF_FB_1}
              label={'Feedback'}
              onClick={() => onResume(`/${VISA_PREP_FORM}/feedback/step-1`)}
            />
          </ChecklistSection>
        </>
      )}
    </>
  );
};

export default VisaPrepForm;
